import React, { useState, useEffect } from 'react';
import api from '../api';
import { useNavigate } from 'react-router-dom';
import SEO from './SEO';
import { Star, MapPin, X, Compass, Sparkles, ChevronRight, ArrowRight, MessageSquare } from 'lucide-react';
import { FaWhatsapp } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import logo from '../assets/logo.webp';
import nepalDest from '../assets/nepal_dest_v2.webp';
import bhutanDest from '../assets/bhutan_dest_v2.webp';
import goaDest from '../assets/goa_dest_v2.webp';
import rajasthanDest from '../assets/rajasthan_dest_v2.webp';
import culturalIndia from '../assets/cultural_india_v1.webp';
import santoriniDest from '../assets/santorini_luxury.webp';
import serengetiDest from '../assets/serengeti_luxury.webp';
import bespokeBg from '../assets/bespoke_bg_v2.webp';

const destinationsHeroBg = "https://images.pexels.com/photos/15286/pexels-photo.jpg?auto=compress&cs=tinysrgb&w=2000";

const DestinationSkeleton = () => (
  <div className="relative overflow-hidden rounded-3xl bg-slate-50 animate-pulse h-[300px] w-full shadow-[0_20px_50px_rgba(0,0,0,0.4)] border border-slate-100">
    <div className="absolute inset-0 bg-gradient-to-t from-slate-200/50 via-transparent to-transparent"></div>
    <div className="absolute bottom-6 left-6 right-6 space-y-4">
      <div className="h-6 bg-slate-200 rounded-full w-2/3"></div>
      <div className="h-4 bg-slate-100 rounded-full w-1/3"></div>
    </div>
  </div>
);

const DestinationCard = ({ dest, index, onClick }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay: index * 0.1 }}
    viewport={{ once: true }}
    onClick={() => onClick(dest)}
    className="group relative h-[300px] rounded-3xl overflow-hidden cursor-pointer shadow-2xl w-full"
  >
    <img
      src={dest.image_url}
      alt={dest.name}
      className="w-full h-full object-cover transition-transform duration-[2s] group-hover:scale-110 hd-image"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/20 to-transparent"></div>

    <div className="absolute top-4 right-4">
      <div className="bg-white/10 backdrop-blur-md px-3 py-1 rounded-full flex items-center gap-1 border border-white/20">
        <Star size={12} className="fill-orange-500 text-orange-500" />
        <span className="text-white text-xs font-black">{dest.rating || 5.0}</span>
      </div>
    </div>

    <div className="absolute bottom-6 left-6 right-6">
      <p className="text-[10px] font-black text-orange-500 uppercase tracking-[0.3em] mb-2">{dest.location || 'Global'}</p>
      <h3 className="text-2xl font-serif font-bold text-white tracking-wide mb-3 line-clamp-1">{dest.name}</h3>

      <div className="flex items-center gap-3 overflow-hidden">
        <div className="h-[1px] w-8 bg-orange-500 group-hover:w-full transition-all duration-500"></div>
        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/60 group-hover:text-white transition-colors shrink-0">View Details</span>
      </div>
    </div>
  </motion.div>
);

const Destinations = () => {
  const [destinations, setDestinations] = useState([]);
  const [selectedDest, setSelectedDest] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmittingCustom, setIsSubmittingCustom] = useState(false);
  const [customSubmitStatus, setCustomSubmitStatus] = useState(null);
  const [showCustomPopup, setShowCustomPopup] = useState(false);
  const [customTripData, setCustomTripData] = useState({
    name: '',
    email: '',
    phone: '',
    place_preference: '',
    description: '',
    budget: ''
  });
  const [showCloseButton, setShowCloseButton] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    let hideTimer;

    const popupTimer = setTimeout(() => {
      setShowCustomPopup(true);
      setShowCloseButton(true);
      // Hide the popup after exactly 2 seconds
      hideTimer = setTimeout(() => {
        setShowCustomPopup(false);
      }, 2000);
    }, 2500);

    const fetchDestinations = async () => {
      try {
        setIsLoading(true);
        const response = await api.get('/destinations');
        setDestinations(response.data);
      } catch (error) {
        console.error('Error fetching destinations:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDestinations();

    return () => {
      clearTimeout(popupTimer);
      if (hideTimer) clearTimeout(hideTimer);
    };
  }, []);

  const scrollToCustomForm = () => {
    navigate('/customised-destination');
  };

  const handleCustomTripSubmit = async (e) => {
    e.preventDefault();
    setIsSubmittingCustom(true);
    try {
      await api.post('/custom-trip', customTripData);
      setCustomSubmitStatus('success');
      setCustomTripData({ name: '', email: '', phone: '', place_preference: '', description: '', budget: '' });
      setTimeout(() => setCustomSubmitStatus(null), 5000);
    } catch (error) {
      console.error('Error submitting custom trip:', error);
      setCustomSubmitStatus('error');
    } finally {
      setIsSubmittingCustom(false);
    }
  };

  const handleWhatsAppContinue = () => {
    const whatsappNumber = "+919880232901";
    const text = `*New Custom Trip Request*
*Name:* ${customTripData.name || 'Not provided'}
*Email:* ${customTripData.email || 'Not provided'}
*Phone:* ${customTripData.phone || 'Not provided'}
*Preference:* ${customTripData.place_preference || 'Not provided'}
*Budget:* ${customTripData.budget || 'Not provided'}

*Description:* ${customTripData.description || 'Not provided'}`;

    const encodedText = encodeURIComponent(text);
    window.open(`https://wa.me/${whatsappNumber.replace(/[^0-9]/g, '')}?text=${encodedText}`, '_blank');
  };

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        title="Luxury Travel Destinations" 
        description="Explore our curated list of international and domestic luxury destinations. From the beaches of Maldives to the culture of Europe, plan your perfect trip with Sunshine Travels." 
        canonicalPath="/destinations" 
      />
      {/* Immersive High-End Hero Section */}
      <section className="relative h-[55vh] flex items-center overflow-hidden pt-20">
        <div className="absolute inset-0 z-0">
          <img
            src={destinationsHeroBg}
            alt="Elite Overwater Resort"
            className="w-full h-full object-cover hd-image"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/10 to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-white to-transparent"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl text-left text-white space-y-4 drop-shadow-2xl">
            <motion.p
              initial={{ opacity: 0, x: -15 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-orange-500 text-[10px] font-black uppercase tracking-[0.4em]"
            >
              Curated Experiences
            </motion.p>
            <div className="space-y-4">
              <motion.h1
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-4xl md:text-6xl font-serif font-bold leading-snug tracking-wide"
              >
                Our <span className="text-orange-500">Destinations</span>
              </motion.h1>
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: 60 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="h-[2px] bg-orange-500 rounded-full"
              ></motion.div>
            </div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-lg md:text-xl text-white/90 font-medium max-w-xl leading-relaxed"
            >
              Discover the world's most extraordinary destinations, <br className="hidden md:block" />
              handpicked and curated for the discerning traveler.
            </motion.p>
          </div>
        </div>
      </section>



      {/* Ultra Compact Grid */}
      <section className="py-16 container mx-auto px-6 max-w-[1600px]">
        <div className="flex items-center justify-between mb-8 border-b border-slate-100 pb-6">
          <div className="flex items-center gap-3">
            <div className="w-1.5 h-8 bg-orange-500 rounded-full"></div>
            <h2 className="text-2xl font-serif font-bold text-slate-900 tracking-wide">Popular <span className="text-orange-500">Destinations</span></h2>
          </div>
          <button
            onClick={scrollToCustomForm}
            className="hidden md:flex items-center gap-2 text-slate-400 text-[10px] font-black uppercase tracking-widest hover:text-orange-500 transition-all group"
          >
            For custom destinations scroll down <ChevronRight size={14} className="text-orange-500 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <AnimatePresence mode='popLayout'>
            {isLoading ? (
              [...Array(12)].map((_, i) => (
                <motion.div
                  key={`skeleton-${i}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  <DestinationSkeleton />
                </motion.div>
              ))
            ) : (
              destinations.map((dest, index) => (
                <DestinationCard key={dest.id} dest={dest} index={index} onClick={setSelectedDest} />
              ))
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* Destination Detail Modal */}
      <AnimatePresence>
        {selectedDest && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedDest(null)}
              className="absolute inset-0 bg-slate-900/95 backdrop-blur-md"
            ></motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 30 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 30 }}
              className="relative bg-white w-full max-w-7xl rounded-[4rem] overflow-hidden shadow-2xl flex flex-col md:flex-row h-[90vh] md:h-[80vh]"
            >
              {/* Image Side - Fixed */}
              <div className="w-full md:w-5/12 h-[350px] md:h-full relative overflow-hidden group shrink-0">
                <img
                  src={selectedDest.image_url}
                  alt={selectedDest.name}
                  className="w-full h-full object-cover transition-transform duration-[3s] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent"></div>


                <div className="absolute bottom-12 left-12 right-12 text-white">
                  <h2 className="text-5xl md:text-7xl font-serif font-bold font-serif font-bold tracking-wide leading-snug drop-shadow-2xl">
                    {selectedDest.name},<br />
                    <span className="text-white/90">{selectedDest.location.split(',').pop().trim()}</span>
                  </h2>
                </div>
              </div>

              {/* Content Side - Scrollable */}
              <div className="w-full md:w-7/12 p-12 md:p-24 flex flex-col bg-white relative overflow-y-auto custom-scrollbar">
                <button
                  onClick={() => setSelectedDest(null)}
                  className="absolute top-10 right-10 z-50 text-slate-300 hover:text-slate-900 transition-colors hover:rotate-90 duration-300"
                >
                  <X size={32} />
                </button>

                <div className="space-y-12 max-w-2xl my-auto">
                  <div className="flex items-center gap-3 text-orange-600 font-black text-xs uppercase tracking-[0.4em]">
                    <MapPin size={16} />
                    <span>{selectedDest.location.split(',').pop().trim()}</span>
                  </div>

                  <div className="space-y-6">
                    <h3 className="text-slate-900 text-3xl font-serif font-bold font-serif font-bold tracking-wide">The Experience</h3>
                    <div className="text-slate-600 text-lg md:text-xl leading-relaxed font-medium space-y-4">
                      {(() => {
                        let gallery = [];
                        try {
                          gallery = JSON.parse(selectedDest.gallery_images || "[]");
                        } catch (e) { }

                        return (selectedDest.description || "Iconic blue domes and sunset views.").split('\n').map((paragraph, idx) => {
                          if (paragraph.startsWith('[IMAGE:')) {
                            const markerVal = paragraph.replace('[IMAGE:', '').replace(']', '').trim();
                            // If marker is a number (index), use gallery, else use it directly (fallback for old ones)
                            const imgUrl = !isNaN(markerVal) && gallery[parseInt(markerVal, 10)]
                              ? gallery[parseInt(markerVal, 10)]
                              : markerVal;

                            if (!imgUrl) return null;
                            return <img key={idx} src={imgUrl} alt="Itinerary" className="w-full h-64 md:h-80 object-cover rounded-3xl shadow-md my-6" />;
                          }
                          if (paragraph.startsWith('Day')) {
                            const [dayPart, ...restPart] = paragraph.split(':');
                            return (
                              <div key={idx} className="bg-slate-50 p-4 rounded-2xl border border-slate-100">
                                <span className="font-black text-orange-600 block text-sm uppercase tracking-widest mb-1">{dayPart}</span>
                                <span className="text-slate-700 text-base">{restPart.join(':').trim()}</span>
                              </div>
                            );
                          }
                          return paragraph.trim() ? <p key={idx}>{paragraph}</p> : null;
                        });
                      })()}
                    </div>
                  </div>

                  <div className="w-full h-[1px] bg-slate-100"></div>

                  <div className="space-y-8">
                    <div className="flex flex-wrap gap-10">
                      <div className="flex items-center gap-3 text-slate-400 text-xs font-black uppercase tracking-widest">
                        <Compass size={18} className="text-orange-500" />
                        <span>Exclusive Itinerary</span>
                      </div>
                      <div className="flex items-center gap-3 text-slate-400 text-xs font-black uppercase tracking-widest">
                        <Sparkles size={18} className="text-orange-500" />
                        <span>Private Access</span>
                      </div>
                    </div>
                  </div>

                  <div className="w-full h-[1px] bg-slate-100"></div>

                  <div className="bg-orange-50 rounded-3xl p-8 border border-orange-100">
                    <h4 className="text-orange-600 font-black uppercase tracking-widest text-sm mb-4">Sunshine Travels Assurance</h4>
                    <p className="text-orange-800/80 text-sm font-medium leading-relaxed mb-6">
                      For detailed inclusions, pricing, and personalized adjustments to this itinerary, please reach out to our travel experts.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 text-sm font-bold text-orange-900">
                      <div className="flex items-center gap-2">
                        <MapPin size={16} className="text-orange-500" />
                        <span>Tiger Circle, Manipal</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-orange-500">📞</span>
                        <span>+91 98802 32901</span>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedDest(null);
                      navigate('/contact');
                    }}
                    className="w-full bg-[#0F172A] text-white py-7 rounded-[2rem] font-black uppercase tracking-[0.3em] text-xs hover:bg-orange-600 transition-all flex items-center justify-center gap-4 shadow-2xl group active:scale-95"
                  >
                    INQUIRE NOW
                    <ChevronRight size={20} className="group-hover:translate-x-2 transition-transform" />
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Final Highlighted CTA Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-slate-900 rounded-[4rem] p-12 md:p-20 text-center relative overflow-hidden group shadow-2xl"
          >
            <div className="absolute inset-0 z-0">
              <img
                src={bespokeBg}
                className="w-full h-full object-cover transition-transform duration-[5s] group-hover:scale-110 opacity-100"
                alt="Signature Journey Background"
              />
              <div className="absolute inset-0 bg-slate-900/60"></div>
            </div>

            {/* Background Accent */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-[120px] -mr-48 -mt-48 z-0"></div>
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-[120px] -ml-48 -mb-48 z-0"></div>

            <div className="relative z-10 space-y-8 max-w-3xl mx-auto">
              <span className="text-orange-500 font-black uppercase tracking-[0.4em] text-[10px] block">Still Searching?</span>
              <h2 className="text-4xl md:text-6xl font-serif font-bold text-white leading-tight">
                Craft Your Own <br />
                <span className="text-orange-500 italic">Signature Journey</span>
              </h2>
              <p className="text-slate-400 text-lg font-medium leading-relaxed">
                If our curated collection doesn't perfectly match your vision, let us design a bespoke itinerary tailored exclusively for you.
              </p>
              <div className="pt-4">
                <button
                  onClick={() => navigate('/customised-destination')}
                  className="bg-orange-500 text-white px-12 py-6 rounded-full font-black uppercase tracking-[0.2em] text-xs hover:bg-white hover:text-slate-900 transition-all shadow-2xl shadow-orange-500/20 group flex items-center gap-4 mx-auto"
                >
                  Customize Your Travel
                  <ArrowRight size={18} className="group-hover:translate-x-2 transition-transform" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
>

      <AnimatePresence>
        {customSubmitStatus && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-8 left-1/2 -translate-x-1/2 px-8 py-4 rounded-2xl shadow-2xl z-50 font-bold ${customSubmitStatus === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
              }`}
          >
            {customSubmitStatus === 'success' ? 'Your custom trip request has been sent! We will contact you soon.' : 'Failed to submit. Please try again.'}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Entry Highlight Popup */}
      <AnimatePresence>
        {showCustomPopup && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-slate-900/60 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              className="bg-white rounded-[2rem] p-6 md:p-8 max-w-sm w-full shadow-2xl relative overflow-hidden"
            >
              {/* Background Decor */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-orange-500/10 rounded-full blur-2xl -mr-16 -mt-16"></div>

              <AnimatePresence>
                {showCloseButton && (
                  <motion.button
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    onClick={() => setShowCustomPopup(false)}
                    className="absolute top-6 right-6 text-slate-300 hover:text-slate-900 transition-colors p-2 hover:bg-slate-100 rounded-full z-50"
                  >
                    <X size={24} />
                  </motion.button>
                )}
              </AnimatePresence>

              <div className="space-y-6 text-center relative z-10">

                <div className="space-y-2 relative">
                  {/* Small Tag removed as it's now main focus above */}
                  <span className="text-orange-500 font-black uppercase tracking-[0.3em] text-[10px] flex items-center justify-center gap-2">
                    <Sparkles size={14} />
                    Exclusive Service
                    <Sparkles size={14} />
                  </span>
                  <h2 className="text-2xl font-serif font-bold text-slate-900">
                    Customize Your <br />
                    Journey, Your Way
                  </h2>
                </div>

                <p className="text-slate-500 text-sm font-medium leading-relaxed">
                  Can't find the perfect itinerary? Our experts can design a unique, bespoke trip just for you.
                </p>

                <div className="pt-4 flex flex-col gap-3">
                  <button
                    onClick={scrollToCustomForm}
                    className="w-full bg-orange-500 text-white py-4 rounded-xl font-bold uppercase tracking-[0.2em] text-[10px] hover:bg-slate-900 transition-all shadow-xl shadow-orange-500/20"
                  >
                    Customise Now
                  </button>
                  <button
                    onClick={() => setShowCustomPopup(false)}
                    className="text-slate-400 font-bold uppercase tracking-widest text-[9px] hover:text-slate-600 transition-colors py-2"
                  >
                    Explore Destinations First
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Destinations;
